import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const statusOptions = [
  { value: '', label: '— No change —' },
  { value: 'Available', label: 'Available' },
  { value: 'Maintenance', label: 'Maintenance' },
];

const BulkEditModal = ({ selectedCount, categories, onClose, onApply }) => {
  const [fields, setFields] = useState({
    status: '',
    category: '',
    notes: '',
  });
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const categoryOptions = [
    { value: '', label: '— No change —' },
    ...(categories || [])?.map(c => ({ value: c?.id, label: c?.name })),
  ];

  const hasChanges = fields?.status || fields?.category || fields?.notes?.trim();

  const handleApply = async () => {
    if (!hasChanges) return;
    setApplying(true);
    setError(null);
    try {
      const updates = {};
      if (fields?.status) updates.status = fields?.status;
      if (fields?.category) updates.category = fields?.category;
      if (fields?.notes?.trim()) updates.notes = fields?.notes?.trim();
      await onApply(updates);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to apply bulk edit');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-2xl border border-border shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="PencilLine" size={18} className="text-accent" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Bulk Edit Resources</h2>
              <p className="text-xs text-muted-foreground">
                Editing {selectedCount} selected resource{selectedCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted/50"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            Only fields you change will be updated. Leave a field at "No change" to keep existing values.
          </p>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Status
            </label>
            <select
              value={fields?.status}
              onChange={e => setFields(prev => ({ ...prev, status: e?.target?.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            >
              {statusOptions?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Category
            </label>
            <select
              value={fields?.category}
              onChange={e => setFields(prev => ({ ...prev, category: e?.target?.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            >
              {categoryOptions?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Notes
              <span className="text-muted-foreground font-normal ml-1">(optional)</span>
            </label>
            <textarea
              value={fields?.notes}
              onChange={e => setFields(prev => ({ ...prev, notes: e?.target?.value }))}
              placeholder="Add a note to all selected resources..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-red-500 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleApply}
            disabled={!hasChanges || applying}
            iconName={applying ? 'Loader2' : 'Check'}
            iconPosition="left"
            iconSize={16}
          >
            {applying ? 'Applying...' : `Apply to ${selectedCount} Resource${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;
